import { useState, useEffect } from 'react'
import { Wifi, WifiOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { apiClient } from '../../services/api'
import { Badge } from './Badge'
import { Button } from './Button'
import toast from 'react-hot-toast'

interface ConnectionStatusProps {
  showDetails?: boolean
  className?: string
}

export const ConnectionStatus = ({ showDetails = false, className = '' }: ConnectionStatusProps) => {
  const [connectionStatus, setConnectionStatus] = useState({
    isOnline: navigator.onLine,
    lastSyncTime: null as Date | null
  })

  useEffect(() => {
    // Listen to browser online/offline events
    const handleOnline = () => {
      setConnectionStatus(prev => ({ ...prev, isOnline: true }))
    }

    const handleOffline = () => {
      setConnectionStatus(prev => ({ ...prev, isOnline: false }))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleForceSync = async () => {
    if (!connectionStatus.isOnline) {
      toast.error('Cannot sync while offline')
      return
    }

    try {
      toast.loading('Checking connection...', { id: 'sync' })
      // Test connection with health check
      await apiClient.healthCheck()
      toast.success('Connection verified!', { id: 'sync' })
      setConnectionStatus(prev => ({ ...prev, lastSyncTime: new Date() }))
    } catch (error: any) {
      toast.error('Connection failed: ' + error.message, { id: 'sync' })
    }
  }

  const formatLastSync = (lastSyncTime: Date | null) => {
    if (!lastSyncTime) return 'Never'
    
    const now = new Date()
    const diff = now.getTime() - lastSyncTime.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  const getStatusColor = () => {
    if (!connectionStatus.isOnline) return 'red'
    return 'green'
  }

  const getStatusText = () => {
    if (!connectionStatus.isOnline) return 'Offline'
    return 'Online'
  }

  const getStatusIcon = () => {
    if (!connectionStatus.isOnline) return <WifiOff className="h-3 w-3" />
    return <Wifi className="h-3 w-3" />
  }

  if (!showDetails) {
    // Compact version for header/toolbar
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Badge 
          variant={getStatusColor() as any}
          className="flex items-center gap-1 text-xs"
          title={`Status: ${getStatusText()}\nLast sync: ${formatLastSync(connectionStatus.lastSyncTime)}`}
        >
          {getStatusIcon()}
          {getStatusText()}
        </Badge>
        {connectionStatus.isOnline && (
          <Button
            variant="ghost"
            size="xs"
            onClick={handleForceSync}
            className="h-6 w-6 p-0"
            title="Test connection"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  // Detailed version for settings/status page
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">Connection Status</h3>
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className={`text-sm font-medium ${
            connectionStatus.isOnline ? 'text-green-600' : 'text-red-600'
          }`}>
            {getStatusText()}
          </span>
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <div className="flex justify-between">
          <span>Connection:</span>
          <div className="flex items-center gap-1">
            {connectionStatus.isOnline ? (
              <>
                <CheckCircle className="h-3 w-3 text-green-500" />
                <span className="text-green-600">Online</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-3 w-3 text-red-500" />
                <span className="text-red-600">Offline</span>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-between">
          <span>Last Sync:</span>
          <span>{formatLastSync(connectionStatus.lastSyncTime)}</span>
        </div>

      </div>

      <div className="mt-4 flex gap-2">
        <Button
          onClick={handleForceSync}
          disabled={!connectionStatus.isOnline}
          size="sm"
          className="flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" />
          Test Connection
        </Button>

        {!connectionStatus.isOnline && (
          <Badge variant="warning" className="text-xs">
            No internet connection
          </Badge>
        )}
      </div>
    </div>
  )
}